import React from 'react';
import { Helmet } from 'react-helmet-async';

import { useTranslation } from 'react-i18next';
import { buildMetaDescription } from '../utils/listingSeo.mjs';

type JsonLd = Record<string, any>;

const getCanonicalUrl = (input?: string) => {
  try {
    const rawBaseUrl = (import.meta.env.VITE_SITE_URL as string) || 'https://www.espacero.com';
    const baseUrl = rawBaseUrl.replace(/\/+$/, '');
    const url = new URL(input || window.location.href, baseUrl);
    url.hash = '';

    const trackingParams = [
      'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid'
    ];
    trackingParams.forEach(p => url.searchParams.delete(p));
    url.search = '';

    // Enforce www.espacero.com for canonical URLs if the current hostname is espacero.com
    if (url.hostname === 'espacero.com') {
      url.hostname = 'www.espacero.com';
    }

    // Normalize: strip trailing slashes for all paths, then always add one back.
    let result = url.toString().replace(/\/+$/, '') + '/';
    
    return result;
  } catch {
    let rawBaseUrl = (import.meta.env.VITE_SITE_URL as string) || 'https://www.espacero.com';
    if (rawBaseUrl.includes('espacero.com') && !rawBaseUrl.includes('www.')) {
      rawBaseUrl = rawBaseUrl.replace('https://', 'https://www.');
    }
    const baseUrl = rawBaseUrl.replace(/\/+$/, '');
    return input || `${baseUrl}/`;
  }
};

interface Breadcrumb {
  name: string;
  item: string;
}

interface SeoProps {
  /** Page title without brand suffix ("espacero" is appended automatically unless exactTitle is true). */
  title: string;
  exactTitle?: boolean;
  /** Optional meta description; falls back to a sensible site-wide default. */
  description?: string;
  image?: string | string[];
  type?: 'website' | 'article' | 'product';
  schema?: JsonLd | JsonLd[];
  canonical?: string;
  noindex?: boolean;
  robots?: string;
  breadcrumbs?: Breadcrumb[];
}

export const Seo: React.FC<SeoProps> = ({
  title,
  exactTitle = false,
  description,
  image = `${(import.meta.env.VITE_SITE_URL as string) || 'https://www.espacero.com'}/og-default.jpg`,
  type = 'website',
  schema,
  canonical,
  noindex = false,
  robots,
  breadcrumbs
}) => {
  const { t } = useTranslation('listing');
  const defaultDescription = t('description_fallback') || 'espacero – Platforma pre prenájom a ponuku voľných priestorov na území Slovenska. Nájdite ideálny priestor pre vašu oslavu, školenie alebo podujatie.';
  const appName = 'Espacero';
  const trimmedTitle = title?.trim() || '';
  const endsWithBrand = trimmedTitle.toLowerCase().endsWith(appName.toLowerCase());
  const fullTitle = exactTitle || endsWithBrand ? trimmedTitle : `${trimmedTitle} | ${appName}`;
  const canonicalUrl = getCanonicalUrl(canonical);
  const schemas = schema ? (Array.isArray(schema) ? [...schema] : [schema]) : [];

  // Add BreadcrumbList if provided
  if (breadcrumbs && breadcrumbs.length > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((b, idx) => ({
        "@type": "ListItem",
        "position": idx + 1,
        "name": b.name,
        "item": b.item
      }))
    });
  }

  // The WebSite node (and its sitelinks SearchAction) is emitted once, site-wide,
  // from index.html via utils/siteEntity.mjs. Repeating it here produced a third
  // spelling of the search URL (?query= vs ?q= in two other places), none of which
  // Google could use because robots.txt disallowed the target.

  // Ensure meta description is of optimal length (ending in a full sentence under 155 chars)
  let rawDescription = description || defaultDescription;
  if (rawDescription.length < 120 && description) {
    const complement = 'Objavte detail priestoru na platforme Espacero.';
    rawDescription = `${rawDescription} ${complement}`;
  }
  const metaDescription = buildMetaDescription(rawDescription, 155);

  React.useEffect(() => {
    if (typeof document === 'undefined') return;

    // 1. Ensure exactly 1 canonical tag in DOM
    const canonicals = Array.from(document.querySelectorAll('link[rel="canonical"]'));
    if (canonicals.length > 1) {
      let kept = false;
      canonicals.forEach(el => {
        const href = el.getAttribute('href');
        if (!kept && (href === canonicalUrl || el.getAttribute('data-rh') === 'true')) {
          kept = true;
          el.setAttribute('href', canonicalUrl);
        } else {
          el.remove();
        }
      });
    }

    // 2. Ensure exactly 1 robots meta tag in DOM
    const robotsEls = Array.from(document.querySelectorAll('meta[name="robots"]'));
    if (robotsEls.length > 1) {
      const rhRobots = robotsEls.find(el => el.getAttribute('data-rh') === 'true');
      if (rhRobots) {
        robotsEls.forEach(el => {
          if (el !== rhRobots) el.remove();
        });
      } else {
        robotsEls.forEach((el, idx) => {
          if (idx > 0) el.remove();
        });
      }
    }

    // 3. Deduplicate JSON-LD script tags by @type (both immediately and after Helmet DOM flush)
    const cleanupJsonLd = () => {
      const jsonLdScripts = Array.from(document.querySelectorAll('script[type="application/ld+json"]'));
      const seenTypes = new Set<string>();
      // Prefer scripts managed by Helmet (data-rh="true"); sort them first so prerendered
      // unmanaged duplicates are removed.
      const sortedScripts = [...jsonLdScripts].sort((a, b) => {
        const aRh = a.getAttribute('data-rh') === 'true' ? 1 : 0;
        const bRh = b.getAttribute('data-rh') === 'true' ? 1 : 0;
        return bRh - aRh;
      });

      sortedScripts.forEach(script => {
        try {
          const text = script.textContent?.trim();
          if (!text) return;
          const data = JSON.parse(text);
          // Preserve site-wide Organization / WebSite @graph from index.html
          if (data['@graph'] && data['@graph'].some((node: any) => node['@type'] === 'Organization' || node['@type'] === 'WebSite')) {
            return;
          }
          const types = [].concat(data['@type'] || []).map(String);
          const typeKey = types.sort().join('+');
          if (!typeKey) return;
          if (seenTypes.has(typeKey)) {
            script.remove();
          } else {
            seenTypes.add(typeKey);
          }
        } catch {}
      });
    };

    cleanupJsonLd();
    const timer = setTimeout(cleanupJsonLd, 60);
    return () => clearTimeout(timer);
  }, [canonicalUrl, fullTitle, metaDescription, schemas]);

  const images = Array.isArray(image) ? image : [image];
  const primaryImage = images[0];

  const currentLocale = 'sk_SK';
  const robotsDirective = robots || (noindex ? 'noindex, follow' : 'index, follow');

  return (
    <Helmet>
      {/* Basic */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="author" content="espacero" />
      <meta name="theme-color" content="#201913" />
      {/* Canonical: omit for noindex pages — SPA routes have no prerendered HTML,
          so browsers fetching the canonical URL would get a 404. */}
      {!noindex && <link rel="canonical" href={canonicalUrl} />}
      <meta name="robots" content={robotsDirective} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={appName} />
      <meta property="og:locale" content={currentLocale} />
      {images.map((img, idx) => (
        <React.Fragment key={`og-image-${idx}`}>
          <meta property="og:image" content={img} />
          <meta property="og:image:alt" content={title} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
        </React.Fragment>
      ))}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      {primaryImage && <meta name="twitter:image" content={primaryImage} />}

      {/* Schema.org JSON-LD */}
      {schemas.map((s, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
};
